/**
 * Sortable, infinitely-scrollable employee table with role, permission, and account management.
 *
 * @module components/admin/EmployeeList
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Avatar from '@material-ui/core/Avatar';
import {makeStyles, withStyles,} from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import InfiniteScroll from 'react-infinite-scroll-component';
import Table from '@material-ui/core/Table';
import Tooltip from '@material-ui/core/Tooltip';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import InputBase from '@material-ui/core/InputBase';
import DeleteIcon from '@material-ui/icons/Delete';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    NativeSelect,
    Paper,
    TextField,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/Edit';
import {useHistory} from 'react-router-dom';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import moment from 'moment';
import {fetchEmployeeList,} from '../../redux/sims/actions';
import noEmployees from '../../assets/images/noEmployees.png';
import {permissionCheck} from '../../utils/permissionCheck';
import AdminsPermissions from '../../permissions/admin';
import {fetchSelectRoles,} from '../../redux/admin/action';
import {loginToAgentAccount} from '../../redux/authentication/action';
import {
    AssignTemporaryRole,
    DeleteTempRole,
    DeleteUserEmployee
} from '../../services/result.service';
import EmployeeListStyle from './EmployeeList';
import ProfilePic from '../../assets/images/profile_pic.png';
import useAlert from "../common/UseAlert";

/**
 * BootstrapInput is a custom-styled input component created using Material-UI's withStyles
 * Higher-Order Component. It is styled to resemble Bootstrap-styled inputs with additional theming
 * integration.
 *
 * The component includes the following custom styles:
 * - `root`: Provides optional margin or padding adjustments when adjacent to a label.
 * - `input`: Applies custom styling such as a specified width, border radius, font size,
 * background color, and a transition effect for focus state transformations.
 *
 * This styled input integrates with Material-UI's theme for consistent design across the
 * application.
 */
const BootstrapInput = withStyles((theme) => ({
    root: {
        'label + &': {},
    },
    input: {
        width: '15.5vw',
        borderRadius: 4,
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        border: '1px solid #ced4da',
        fontSize: 13,
        fontWeight: '500',
        padding: 10,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:focus': {
            borderRadius: 4,
            borderColor: '#80bdff',
            boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
        },
    },
}))(InputBase);

/**
 * A styled table row component that applies custom styles to alternating rows
 * within a table. The row uses the theming system to ensure consistent appearance
 * adhering to the application's design.
 *
 * Key Features:
 * - Alternates the background color for even rows to enhance table readability.
 * - Leverages the theme's palette action hover color for consistent styling.
 *
 * Inherits functionality from the Material-UI `TableRow` and `TableCell` components.
 *
 * @constant
 */
const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow, TableCell);

/**
 * A function component that manages employee data and operations within the application.
 * The `EmployeeList` component provides functionalities for displaying, modifying, and managing
 * employee-related information, including roles, sorting, and permissions. It incorporates hooks,
 * state management, and various interactions to support system-wide workflows.
 *
 * Features:
 * - Displays a list of employees and supports sorting and ordering based on configurable
 * properties.
 * - Manages temporary roles, including assignment, viewing, and deletion for specific employees.
 * - Handles role selection through dynamically fetched roles and integrates these roles into
 * system workflows.
 * - Provides navigation and interaction capabilities, such as creating a user or managing roles.
 *
 * Props:
 * - `empList` (Array): The list of employees to be displayed.
 * - `total` (number): The total number of employees in the system.
 * - `handleNext` (Function): Callback function to trigger the next step in the workflow.
 * - `createUser` (Function): Function to create a new user within the system.
 * - `employee_listing` (Function): Function to manage employee listings in the system.
 * - `fetchSelectRoles` (Function): Function to fetch available roles for selection.
 * - `selectRoles` (Array): Predefined roles available for selection.
 * - `addTempRole` (Function): Function to add a temporary role to an employee.
 * - `viewPermission` (Function): Function to view permission settings for an employee.
 * - `deleteEmployee` (Function): Function to delete an employee from the system.
 * - `loginToAgentAccount` (Function): Function to log into an employee's (agent) account.
 * - `sortProperties` (Array): Array of properties by which the employee list can be sorted.
 * - `sortBy` (string): Defines the property currently used for sorting the list.
 * - `order` (string): Determines the order of sorting (e.g., ascending or descending).
 *
 * State Variables:
 * - `tempRole` (boolean): Indicates if the temporary role dialog is open.
 * - `allRoles` (Array): Stores all available roles for assignment and selection.
 * - `currentRole` (string): Represents the role currently being operated upon.
 * - `roles` (Array): Maintains a list of roles (primary or temporary) associated with a user.
 * - `tempRoleData` (Object): Tracks temporary role data, including start and end dates.
 * - `sort` (boolean): A flag to determine if sorting operations need to be triggered or reset.
 *
 * Dependencies:
 * - `moment`: Library used for date formatting.
 * - `makeStyles`: Styling utility to apply theme-related designs.
 * - `useHistory`: Hook to manage navigation within the application.
 * - `useAlert`: Custom hook to set application-level alerts/notifications.
 *
 * This component uses React hooks (`useState`, `useEffect`) extensively to manage internal state,
 * lifecycle methods, and side effects triggered by prop changes or user interactions.
 */
const EmployeeList = ({
    empList, total,
    handleNext,
    createUser,
    employee_listing,
    fetchSelectRoles,
    selectRoles,
    addTempRole,
    viewPermission,
    deleteEmployee,
    loginToAgentAccount,
    sortProperties,
    sortBy,
    order,
}) => {
    const today = moment(new Date()).format('YYYY-MM-DD');
    const useStyles = makeStyles((theme) => EmployeeListStyle(theme));
    const classes = useStyles();
    const history = useHistory();
    const {setAlert} = useAlert();

    const [tempRole, setTempRole] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [currentRole, setCurrentRole] = useState('');
    const [roles, setRoles] = useState([]);
    const [tempRoleuser, setTempRoleUser] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteEmployeeModal, setDeleteEmployeeModal] = useState(false);
    const [selectedUser, setSelecteddUser] = useState({});
    const [viewTempRole, setViewTempRole] = useState(false);
    const [tempRoles, setTempRoles] = useState([]);
    const [sort, setSort] = useState(false);

    const [tempRoleData, setTempRoleData] = useState({
        role: '',
        startDate: '',
        endDate: '',
    });

    useEffect(() => {
        fetchSelectRoles();
    }, [fetchSelectRoles]);

    useEffect(() => {
        if (empList.length && sort) {
            setSort(false);
        }
    }, [empList.length, sort]);

    useEffect(() => {
        if (selectRoles && roles.length) {
            setAllRoles(selectRoles);
        }
        if (allRoles.length) {
            setTempRoleData({
                ...tempRoleData,
                role: allRoles[0]._id,
            });
        }
    }, [selectRoles, roles.length]);

    /**
     * A function that triggers the `handleNext` operation.
     * Typically used as an event handler for managing changes
     * and proceeding to the next action or step in sequence.
     */
    const handleChange = () => {
        handleNext();
    };

    /**
     * Handles the click event for a given row.
     * Navigates to the Create User page with the user ID of the selected row as a query parameter.
     *
     * @param {Object} row - The data object for the clicked row.
     * @param {string} row._id - The unique identifier of the user in the row.
     */
    const handleClick = (row) => {
        history.push(`/admin/createuser?userId=${row._id}`);
    };

    /**
     * Handles the temporary role assignment for a user.
     *
     * This function is used to manage and store the temporary roles associated
     * with a specific user. It sets appropriate state values to indicate that
     * a temporary role is being assigned and collects all relevant roles,
     * including the user's primary role and any additional temporary roles.
     *
     * @param {Object} row - The data object containing user and role information.
     * @param {string} row._id - The unique identifier of the user.
     * @param {string} row.role_id - The primary role identifier of the user.
     * @param {Array} [row.tempRoles] - An optional array of objects representing
     * temporary roles assigned to the user.
     * @param {string} row.tempRoles[].role - The identifier of a temporary role.
     */
    const openTempRole = (row) => {
        setTempRole(true);
        setTempRoleUser(row._id);
        const data = [];
        data.push(row.role_id);
        if (row.tempRoles && row.tempRoles.length) {
            row.tempRoles.map((tmpRole) => {
                data.push(tmpRole.role);
            });
        }
        setRoles(data);
    };

    /**
     * Function to handle the closure of a specific process or interaction.
     * It performs the following operations:
     * - Resets the temporary role state to `false`.
     * - Clears the roles by setting the roles array to an empty array.
     * - Resets the temporary role user to an empty string.
     */
    const handleClose = () => {
        setTempRole(false);
        setRoles([]);
        setTempRoleUser('');
    };

    /**
     * Handles the change event for selecting a role.
     * Updates the temporary role data state with the new value based on the event target's name.
     *
     * @param {Object} evt - The event object fired by the input element.
     * @param {Object} evt.target - The target element that triggered the event.
     * @param {string} evt.target.name - The name attribute of the target element.
     * @param {string} evt.target.value - The new value of the input element.
     */
    const handleRoleChange = (evt) => {
        const {value} = evt.target;
        setTempRoleData({
            ...tempRoleData,
            [evt.target.name]: value,
        });
    };

    /**
     * Asynchronous function to assign a temporary role to a user.
     *
     * Invokes the `AssignTemporaryRole` function with provided parameters
     * and performs actions based on the response. If the operation is successful,
     * it updates relevant state or triggers additional processes.
     *
     * @function
     * @async
     * @returns {Promise<void>} A promise that resolves when the temporary role assignment is
     *     complete.
     */
    const AsssignTempRole = async () => {
        await AssignTemporaryRole(tempRoleData, tempRoleuser).then((res) => {
            if (res.data) {
                setTempRole(false);
                addTempRole();
            }
        });
    };

    /**
     * Asynchronous function to delete a temporary role.
     *
     * This function executes the `DeleteTempRole` operation, which removes a temporary role
     * assigned to a user. Upon successful deletion, the function performs several actions:
     * - Closes the delete confirmation modal.
     * - Resets the temporary role user to an empty string.
     * - Hides the temporary role view.
     * - Clears the current role information.
     * - Invokes the `addTempRole` function for further handling.
     *
     * Dependencies:
     * - `DeleteTempRole`: A function that handles the deletion of the temporary role.
     * - `setDeleteModal`: A state updater function that controls the visibility of the delete
     * modal.
     * - `setTempRoleUser`: A function to reset the temporary role's user identifier.
     * - `setViewTempRole`: A function to hide the temporary role view.
     * - `setCurrentRole`: A function to reset the currently assigned role.
     * - `addTempRole`: A function invoked after the deletion process.
     */
    const deleteTempRole = async () => {
        await DeleteTempRole(currentRole, tempRoleuser).then((/*res*/) => {
            setDeleteModal(false);
            setTempRoleUser('');
            setViewTempRole(false);
            setCurrentRole('');
            addTempRole();
        });
    };

    /**
     * Closes the delete modal and resets related state variables.
     *
     * This function is used to handle the closing of the delete modal by:
     * - Setting the state of the delete modal to false, effectively hiding it.
     * - Resetting the current role to an empty string.
     *
     * The purpose is to ensure that the delete modal is properly closed and any
     * associated data (e.g., selected role information) is cleared.
     */
    const handleCloseDeleteModal = () => {
        setDeleteModal(false);
        setCurrentRole('');
    };

    /**
     * Triggers the opening of the delete modal and sets the current role.
     *
     * @param {Object} tmprole - The role to be set as the current role
     *                           before opening the delete modal.
     */
    const openDeleteModal = (tmprole) => {
        setCurrentRole(tmprole);
        setDeleteModal(true);
    };

    /**
     * Opens the temporary role modal and sets the necessary state for viewing or managing
     * temporary roles for a specific user.
     *
     * @param {Object} row - An object containing the data of the selected user.
     * @param {string} row._id - The unique identifier of the selected user.
     * @param {Array} row.tempRoles - The list of temporary roles associated with the selected
     *     user.
     */
    const openViewTempRoleModal = (row) => {
        setViewTempRole(true);
        setTempRoleUser(row._id);
        setTempRoles(row.tempRoles);
    };

    /**
     * Closes the delete employee modal and resets relevant state variables.
     *
     * This function is responsible for setting the state variable
     * that controls the visibility of the delete employee modal to `false`.
     * Additionally, it clears the currently selected user by resetting
     * the `selectedUser` state variable to an empty object.
     *
     * Typical use case involves calling this function when the user
     * wishes to cancel or close the delete employee modal dialog.
     */
    const handleCloseDeleteEmployeeModal = () => {
        setDeleteEmployeeModal(false);
        setSelecteddUser({});
    };

    /**
     * Handles the confirmation process for deleting an employee.
     *
     * @param {Object} row - The data object representing the employee to be deleted.
     * Sets the selected user to the provided row and triggers the visibility of the delete
     *     confirmation modal.
     */
    const deleteEmployeeConfirm = (row) => {
        setSelecteddUser(row);
        setDeleteEmployeeModal(true);
    };

    /**
     * Asynchronously logs in as an agent using the provided row data.
     *
     * This function is responsible for initiating a login process
     * using the provided row object, which contains the required
     * credentials for the agent account.
     *
     * @param {Object} row - An object containing the agent's account details.
     * @param {string} row._id - The unique identifier of the agent.
     * @param {string} row.email - The email address of the agent.
     * @returns {Promise<void>} A promise that resolves once the login process is complete.
     */
    const loginAsAgent = async (row) => {
        await loginToAgentAccount({id: row._id, email: row.email})
    }

    /**
     * Asynchronous function to handle the deletion of an employee user.
     *
     * This function performs the following steps:
     * 1. Closes the "Delete Employee" modal.
     * 2. Calls the `DeleteUserEmployee` API with the selected user's ID to remove the employee
     * user.
     * 3. Checks the API response for success or error:
     *    - If the deletion is unsuccessful, it displays an error alert with the returned error
     * message and scrolls the window to the top.
     *    - If the deletion is successful, it invokes the `deleteEmployee` method to finalize the
     * operation.
     */
    const deleteEmployeeUser = async () => {
        setDeleteEmployeeModal(false);
        await DeleteUserEmployee(selectedUser._id).then((res) => {
            if (!res.data.success) {
                setAlert(res.data.message, 'error');
                window.scrollTo(0, 0);
            } else {
                deleteEmployee();
            }
        });
    };

    /**
     * Handles copying a given ID to the system clipboard.
     *
     * This asynchronous function writes the provided ID to the clipboard
     * and notifies the user of the action outcome through a visual alert.
     * If an error occurs during the process, it logs the error message to the console.
     *
     * @param {string} id - The identifier to be copied to the clipboard.
     */
    const onCopyIdHandler = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
            setAlert('User ID copied to clipboard', 'info');
        } catch (error) {
            console.error(error.message);
        }
    }

    return (
        <>
            {(employee_listing && empList.length === 0) ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '70vh',
                    }}
                >
                    <CircularProgress color="primary"/>
                </div>
            ) : ''}
            <div className={classes.outer}>
                {empList.length === 0 && !sort
                    ? (
                        <div className={classes.noData}>
                            <img
                                src={noEmployees}
                                height={55}
                                width={54}
                                alt="logo"
                            />
                            <h3 className={classes.noAssign}>
                                No Employees added in your list yet!
                            </h3>
                            <p className={classes.noAssignPara}>Let&quot;s add your first employee
                                details</p>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.addButton}
                                onClick={createUser}
                            >
                                <span className={classes.plus}>+</span>
                                {' '}
                                ADD NEW USER
                            </Button>
                        </div>
                    )
                    : (
                        <div className={classes.inner}>
                            <InfiniteScroll
                                dataLength={empList.length}
                                next={handleChange}
                                hasMore={empList.length < total}
                                loader={<p>Loading...</p>}
                            >
                                <TableContainer component={Paper} className={classes.root}>
                                    <Table aria-label="sims table" className={classes.table}>
                                        <TableHead className={classes.thead}>
                                            <TableRow>
                                                <TableCell padding="none"
                                                           className={classes.hard_left_action}>
                                                    ACTIONS
                                                </TableCell>

                                                <TableCell
                                                    padding="none"
                                                    className={classes.hard_left_head}
                                                >
                                                    <TableSortLabel
                                                        direction={sortBy === 'firstName' ?
                                                            order :
                                                            'asc'}
                                                        active
                                                        IconComponent={ArrowDropDownIcon}
                                                        onClick={() => {
                                                            sortProperties('first_name');
                                                            setSort(true);
                                                        }}
                                                    >
                                                        FIRST NAME
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell
                                                    padding="none"
                                                    className={classes.next_left_head}
                                                >
                                                    <TableSortLabel
                                                        direction={sortBy === 'lastName' ?
                                                            order :
                                                            'asc'}
                                                        active
                                                        IconComponent={ArrowDropDownIcon}
                                                        onClick={() => {
                                                            sortProperties('last_name');
                                                            setSort(true);
                                                        }}
                                                    >
                                                        LAST NAME
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell padding="none"
                                                           className={classes.heading}>
                                                    USER ID
                                                </TableCell>

                                                <TableCell padding="none"
                                                           className={classes.heading}>
                                                    EMAIL
                                                </TableCell>

                                                <TableCell className={classes.role}>
                                                    <TableSortLabel
                                                        active
                                                        IconComponent={ArrowDropDownIcon}
                                                        direction={sortBy === 'role' ?
                                                            order :
                                                            'asc'}
                                                        onClick={() => {
                                                            sortProperties('role');
                                                            setSort(true);
                                                        }}
                                                    >
                                                        ROLE
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell className={classes.permissions}
                                                           padding="none">
                                                    PERMISSIONS
                                                </TableCell>

                                                <TableCell className={classes.tempRole}>
                                                    TEMP ROLES
                                                </TableCell>

                                                <TableCell padding="none"
                                                           className={classes.heading}>
                                                    GROUPS
                                                </TableCell>

                                                <TableCell className={classes.heading}>
                                                    <TableSortLabel
                                                        active
                                                        IconComponent={ArrowDropDownIcon}
                                                        direction={sortBy === 'status' ?
                                                            order :
                                                            'asc'}
                                                        onClick={() => {
                                                            sortProperties('status');
                                                            setSort(true);
                                                        }}
                                                    >
                                                        STATUS
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell padding="none"
                                                           className={classes.heading}>
                                                    <TableSortLabel
                                                        active
                                                        IconComponent={ArrowDropDownIcon}
                                                        direction={sortBy === 'title' ?
                                                            order :
                                                            'asc'}
                                                        onClick={() => {
                                                            sortProperties('title');
                                                            setSort(true);
                                                        }}
                                                    >
                                                        TITLE
                                                    </TableSortLabel>
                                                </TableCell>

                                                <TableCell className={classes.heading}>
                                                    PHONE NUMBER
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody className={classes.tbody}>
                                            {empList && empList.map((row, index) => (
                                                <StyledTableRow
                                                    key={index}
                                                    className={classes.tableRow}
                                                >
                                                    <TableCell
                                                        className={(index % 2) !== 0
                                                            ?
                                                            classes.even_left_action :
                                                            classes.hard_left_action_tbody}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                !permissionCheck(
                                                                    AdminsPermissions.employee_list.update.task,
                                                                    AdminsPermissions.employee_list.update.type,
                                                                )
                                                                    ?
                                                                    "You don't have permission to Edit"
                                                                    :
                                                                    'Edit'
                                                            }
                                                            placement="top"
                                                        >
                              <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    className={permissionCheck(
                                        AdminsPermissions.employee_list.update.task,
                                        AdminsPermissions.employee_list.update.type,
                                    ) ? classes.buttonSqr : classes.btnDisableSqr}
                                    disableElevation
                                    onClick={() => handleClick(row)}
                                >
                                  <EditIcon
                                      style={{fontSize: 15}}
                                  />
                                </Button>
                              </span>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={
                                                                !permissionCheck(
                                                                    AdminsPermissions.employee_list.delete.task,
                                                                    AdminsPermissions.employee_list.delete.type,
                                                                )
                                                                    ?
                                                                    "You don't have permission to Delete"
                                                                    :
                                                                    'Delete'
                                                            }
                                                            placement="top"
                                                        >
                              <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    className={permissionCheck(
                                        AdminsPermissions.employee_list.delete.task,
                                        AdminsPermissions.employee_list.delete.type,
                                    ) ? classes.buttonEditSqr : classes.btnEditDisableSqr}
                                    disableElevation
                                    onClick={() => deleteEmployeeConfirm(row)}
                                >
                                  <DeleteIcon
                                      style={{fontSize: 15}}
                                  />
                                </Button>
                              </span>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title="Login"
                                                            placement="top"
                                                        >
                              <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    className={(row.role).toLowerCase() === 'agent' ?
                                        classes.buttonEditSqr :
                                        classes.btnLogin}
                                    onClick={() => loginAsAgent(row)}>
                                  <HowToRegIcon style={{fontSize: 15}}/>
                                </Button>
                              </span>
                                                        </Tooltip>
                                                    </TableCell>

                                                    <TableCell
                                                        padding="none"
                                                        className={
                                                            (index % 2) !== 0 ?
                                                                classes.even_left :
                                                                classes.hard_left
                                                        }
                                                    >
                                                        <div className={classes.built}>
                                                            <Avatar
                                                                src={row?.profile_pic_url ?
                                                                    row.profile_pic_url :
                                                                    ProfilePic}
                                                                alt="profile"
                                                                style={{marginRight: '10px'}}
                                                                className={classes.small}
                                                            />
                                                            <div
                                                                className={classes.firstNameCell}>{row.first_name}</div>
                                                        </div>
                                                    </TableCell>

                                                    <TableCell
                                                        padding="none"
                                                        className={
                                                            (index % 2) !== 0 ?
                                                                classes.even_next_left :
                                                                classes.next_left
                                                        }
                                                    >
                                                        {row.last_name}
                                                    </TableCell>

                                                    <TableCell passing="none">
                                                        {row._id.toString()}
                                                        &nbsp;
                                                        <Tooltip
                                                            title="Copy user's ID to clipboard">
                                                            <FileCopyIcon
                                                                onClick={() => onCopyIdHandler(row._id.toString())}/>
                                                        </Tooltip>
                                                    </TableCell>

                                                    <TableCell className={classes.emailIdCell}
                                                               padding="none">{row.email}</TableCell>

                                                    <TableCell
                                                        className={classes.roleCell}>{row.role}</TableCell>

                                                    <TableCell align="left"
                                                               className={classes.permissionsTableCell}
                                                               onClick={() => viewPermission(row)}>VIEW</TableCell>

                                                    <TableCell align="left"
                                                               style={{textAlign: 'center'}}>
                                                        {row.tempRoles && row.tempRoles.length
                                                            ? (
                                                                <span
                                                                    className={classes.permissionsTableCell}
                                                                    onClick={() => openViewTempRoleModal(
                                                                        row)}
                                                                >
                                  VIEW TEMP ROLES
                                </span>
                                                            ) : ''}
                                                        <AddIcon
                                                            className={classes.addIcon}
                                                            onClick={() => openTempRole(row)}
                                                        />
                                                    </TableCell>

                                                    <TableCell className={classes.noWrap}>
                                                        {(row.groups.toString()).replace(/,/g,
                                                            ', '
                                                        )}
                                                    </TableCell>

                                                    <TableCell
                                                        className={classes.statusCell}>{row.status}</TableCell>

                                                    <TableCell className={classes.titleCell}
                                                               padding="none"
                                                               align="left">{row.title}</TableCell>

                                                    <TableCell className={classes.phoneNumberCell}
                                                               align="left">{row.phone_number}</TableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </InfiniteScroll>
                        </div>
                    )}
            </div>
            <Dialog
                open={deleteEmployeeModal}
                onClose={handleCloseDeleteEmployeeModal}
                aria-labelledby="alert-dialog-title"
                className={classes.Deletedialog}
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Delete Employee</DialogTitle>
                <DialogContent>
                    <DialogContentText className={classes.text}>
                        {`Are you sure you want to delete employee - ${selectedUser.first_name} ${selectedUser.last_name}?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDeleteEmployeeModal}
                        className={classes.cancelButton}
                    >
                        CANCEL
                    </Button>
                    <Button
                        autoFocus
                        className={classes.deleteEmployee}
                        onClick={deleteEmployeeUser}
                    >
                        DELETE
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={tempRole}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                className={classes.dialog}
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Assign Temporary Role</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={4} className={classes.centerLabel}>
                            Role
                        </Grid>
                        <Grid item xs={8}>
                            <NativeSelect
                                lableid="selectLable"
                                id="selectRole"
                                name="role"
                                input={<BootstrapInput/>}
                                onChange={handleRoleChange}
                                variant="outlined"
                                required
                                fullWidth
                                className={classes.arrowIcon}
                                IconComponent={KeyboardArrowDownIcon}
                            >
                                {allRoles.map((role) => (
                                    <option key={role._id} value={role._id}>
                                        {role.name}
                                    </option>
                                ))}
                            </NativeSelect>
                        </Grid>
                        <Grid item xs={4} className={classes.centerLabel}>
                            Start Date
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                id="date"
                                onChange={handleRoleChange}
                                type="date"
                                name="startDate"
                                defaultValue="dd/mm/yyyy"
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{
                                    min: today,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.centerLabel}>
                            End Date
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                id="date"
                                onChange={handleRoleChange}
                                type="date"
                                name="endDate"
                                defaultValue="dd/mm/yyyy"
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{
                                    min: today,
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Tooltip
                        title={
                            !permissionCheck(
                                AdminsPermissions.roles.update.task,
                                AdminsPermissions.roles.update.type,
                            )
                                ? "You don't have permission to update the role"
                                : ''
                        }
                        placement="top"
                    >
            <span>
              <Button
                  autoFocus
                  className={classes.assignRole}
                  onClick={AsssignTempRole}
                  disabled={tempRoleData.role === ''
                      || tempRoleData.startDate === ''
                      || tempRoleData.endDate === ''
                      || !permissionCheck(
                          AdminsPermissions.roles.update.task,
                          AdminsPermissions.roles.update.type,
                      )}
              >
                Assign Role
          </Button>
            </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteModal}
                onClose={handleCloseDeleteModal}
                aria-labelledby="alert-dialog-title"
                className={classes.Deletedialog}
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this temporary role
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDeleteModal}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        autoFocus
                        className={classes.assignRole}
                        onClick={deleteTempRole}
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={viewTempRole}
                onClose={() => setViewTempRole(false)}
                aria-labelledby="alert-dialog-title"
                className={classes.dialog}
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">View Temporary Role</DialogTitle>
                <DialogContent>
                    <Table aria-label="sims table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Temp Role</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tempRoles && tempRoles.map((tmpRole) => (
                                <StyledTableRow key={tmpRole.role}>
                                    <TableCell
                                        style={{whiteSpace: 'nowrap'}}>{tmpRole.roleName}</TableCell>
                                    <TableCell>{tmpRole.startDate}</TableCell>
                                    <TableCell>{tmpRole.endDate}</TableCell>
                                    <TableCell>
                                        <DeleteIcon
                                            className={classes.delete}
                                            onClick={() => openDeleteModal(tmpRole.role)}
                                            fontSize="small"
                                        />
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setViewTempRole(false);
                        }}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

EmployeeList.propTypes = {
    empList: PropTypes.array,
    total: PropTypes.number,
    handleNext: PropTypes.func,
    createUser: PropTypes.func,
    fetchSelectRoles: PropTypes.func,
    employee_listing: PropTypes.bool,
    selectRoles: PropTypes.array,
    addTempRole: PropTypes.func,
    deleteEmployee: PropTypes.func,
    viewPermission: PropTypes.func,
    loginToAgentAccount: PropTypes.func,
    sortProperties: PropTypes.func,
    sortBy: PropTypes.string,
    order: PropTypes.string,
};

const mapStateToProps = (state) => ({
    membersList: state.sim.membersList,
    employee_listing: state.sim.employee_listing,
    selectRoles: state.admin.selectRoles,
});

const mapDispatchToProps = {fetchEmployeeList, fetchSelectRoles, loginToAgentAccount};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeList);
